import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress,
  Alert, InputAdornment, ToggleButtonGroup, ToggleButton, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import StatsCard from '../../components/StatsCard';
import { getCoinStats, adjustUserCoins, updateCoinSettings, getCoinPacks, createCoinPack, updateCoinPack, deleteCoinPack } from '../../api/endpoints/coins';

const defaultRates = { chat: 10, call: 15, video: 20, kundli: 50, matchmaking: 30 };

const CoinManagement = () => {
  const [stats, setStats] = useState(null);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [conversionRate, setConversionRate] = useState(10);
  const [spendingRates, setSpendingRates] = useState(defaultRates);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Manual adjust state
  const [adjustUserId, setAdjustUserId] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustType, setAdjustType] = useState('credit');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Pack dialog
  const [packDialog, setPackDialog] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  const [packForm, setPackForm] = useState({ coins: '', price: '', bonus: 0, label: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, packsRes] = await Promise.all([getCoinStats(), getCoinPacks()]);
      setStats(statsRes.data.data);
      setPacks(packsRes.data.data || []);
    } catch {
      toast.error('Failed to load coin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUserSearch = async (query) => {
    if (query.length < 2) { setUserSearchResults([]); return; }
    try {
      setSearchLoading(true);
      const { default: api } = await import('../../api/endpoints/users');
      const res = await api.getUsers({ search: query, limit: 5 });
      setUserSearchResults(res.data.data || []);
    } catch {
      // ignore
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAdjustCoins = async () => {
    if (!selectedUser || !adjustAmount || !adjustReason) {
      toast.error('Please select a user, enter amount and reason');
      return;
    }
    try {
      setAdjusting(true);
      await adjustUserCoins({
        userId: selectedUser._id,
        amount: parseInt(adjustAmount, 10),
        type: adjustType,
        reason: adjustReason,
      });
      toast.success(`Coins ${adjustType}ed successfully`);
      setAdjustUserId('');
      setAdjustAmount('');
      setSelectedUser(null);
      setUserSearchResults([]);
      fetchData();
    } catch {
      toast.error('Failed to adjust coins');
    } finally {
      setAdjusting(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsLoading(true);
      await updateCoinSettings({ coin_conversion_rate: parseInt(conversionRate, 10), coin_spending_rates: spendingRates });
      toast.success('Coin settings updated');
    } catch {
      toast.error('Failed to update settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSavePack = async () => {
    try {
      const data = { coins: parseInt(packForm.coins, 10), price: parseInt(packForm.price, 10), bonus: parseInt(packForm.bonus, 10) || 0, label: packForm.label };
      if (editingPack) {
        await updateCoinPack(editingPack.id, data);
        toast.success('Coin pack updated');
      } else {
        await createCoinPack(data);
        toast.success('Coin pack created');
      }
      setPackDialog(false);
      setEditingPack(null);
      setPackForm({ coins: '', price: '', bonus: 0, label: '' });
      fetchData();
    } catch {
      toast.error('Failed to save coin pack');
    }
  };

  const handleDeletePack = async (packId) => {
    if (!window.confirm('Delete this coin pack?')) return;
    try {
      await deleteCoinPack(packId);
      toast.success('Coin pack deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete coin pack');
    }
  };

  const openEditPack = (pack) => {
    setEditingPack(pack);
    setPackForm({ coins: pack.coins, price: pack.price, bonus: pack.bonus || 0, label: pack.label || '' });
    setPackDialog(true);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <PageHeader title="Coin Management" subtitle="Manage coin system, packs, and user coin adjustments" />

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <StatsCard icon={MonetizationOnIcon} label="Total Coins in Circulation" value={stats?.totalCoinsInCirculation?.toLocaleString() || 0} color="#2D1B69" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatsCard icon={TrendingUpIcon} label="Coins Earned Today" value={stats?.coinsEarnedToday?.toLocaleString() || 0} color="#4CAF50" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatsCard icon={TrendingDownIcon} label="Coins Spent Today" value={stats?.coinsSpentToday?.toLocaleString() || 0} color="#F44336" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Coin Packs</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { setEditingPack(null); setPackForm({ coins: '', price: '', bonus: 0, label: '' }); setPackDialog(true); }} sx={{ mb: 2 }}>
                Add Pack
              </Button>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Label</TableCell>
                      <TableCell align="right">Coins</TableCell>
                      <TableCell align="right">Price (₹)</TableCell>
                      <TableCell align="right">Bonus</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {packs.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary">No coin packs configured</Typography></TableCell></TableRow>
                    ) : packs.map((pack) => (
                      <TableRow key={pack.id} hover>
                        <TableCell>{pack.label || pack.id}</TableCell>
                        <TableCell align="right">{pack.coins}</TableCell>
                        <TableCell align="right">₹{pack.price}</TableCell>
                        <TableCell align="right">{pack.bonus > 0 ? <Chip label={`+${pack.bonus}`} color="success" size="small" /> : '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditPack(pack)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleDeletePack(pack.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Coin Rates</Typography>
              <Stack spacing={2}>
                <TextField label="Coin to INR Rate" type="number" size="small" value={conversionRate}
                  onChange={(e) => setConversionRate(e.target.value)}
                  helperText="1 coin = 1/rate INR. Default: 10" />
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Per-Service Coin Spending Rates (coins per unit)</Typography>
                {Object.entries(defaultRates).map(([key]) => (
                  <TextField key={key} label={key.charAt(0).toUpperCase() + key.slice(1)} type="number" size="small"
                    value={spendingRates[key] || ''}
                    onChange={(e) => setSpendingRates(prev => ({ ...prev, [key]: parseInt(e.target.value, 10) || 0 }))} />
                ))}
                <Button variant="contained" onClick={handleSaveSettings} disabled={settingsLoading} sx={{ mt: 1 }}>
                  {settingsLoading ? <CircularProgress size={20} /> : 'Save Settings'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Manual Coin Adjustment</Typography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Search User" size="small" value={adjustUserId}
                    onChange={(e) => { setAdjustUserId(e.target.value); handleUserSearch(e.target.value); }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                    helperText="Search by name or phone" />
                  {userSearchResults.length > 0 && (
                    <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 150, overflow: 'auto' }}>
                      {userSearchResults.map(u => (
                        <Box key={u._id} sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          onClick={() => { setSelectedUser(u); setAdjustUserId(`${u.name || 'Unknown'} (${u.phone || '-'})`); setUserSearchResults([]); }}>
                          <Typography variant="body2">{u.name || 'Unknown'} - {u.phone || '-'}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                  {selectedUser && <Chip label={`Selected: ${selectedUser.name || selectedUser.phone} (Coins: ${selectedUser.coins || 0})`} color="primary" size="small" sx={{ mt: 0.5 }} onDelete={() => { setSelectedUser(null); setAdjustUserId(''); }} />}
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField fullWidth label="Amount" type="number" size="small" value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)} />
                </Grid>
                <Grid item xs={6} md={2}>
                  <ToggleButtonGroup value={adjustType} exclusive onChange={(e, v) => v && setAdjustType(v)} size="small" fullWidth>
                    <ToggleButton value="credit" sx={{ color: 'success.main' }}>Credit</ToggleButton>
                    <ToggleButton value="debit" sx={{ color: 'error.main' }}>Debit</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Reason</InputLabel>
                    <Select value={adjustReason} label="Reason" onChange={(e) => setAdjustReason(e.target.value)}>
                      <MenuItem value="Support adjustment">Support adjustment</MenuItem>
                      <MenuItem value="Bonus credit">Bonus credit</MenuItem>
                      <MenuItem value="Bug compensation">Bug compensation</MenuItem>
                      <MenuItem value="Fraud deduction">Fraud deduction</MenuItem>
                      <MenuItem value="Manual correction">Manual correction</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button variant="contained" fullWidth onClick={handleAdjustCoins} disabled={adjusting || !selectedUser}>
                    {adjusting ? <CircularProgress size={20} /> : 'Apply'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={packDialog} onClose={() => setPackDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPack ? 'Edit Coin Pack' : 'Add Coin Pack'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Pack Label" size="small" value={packForm.label}
              onChange={(e) => setPackForm(prev => ({ ...prev, label: e.target.value }))} />
            <TextField label="Coins" type="number" size="small" value={packForm.coins}
              onChange={(e) => setPackForm(prev => ({ ...prev, coins: e.target.value }))} />
            <TextField label="Price (₹)" type="number" size="small" value={packForm.price}
              onChange={(e) => setPackForm(prev => ({ ...prev, price: e.target.value }))} />
            <TextField label="Bonus Coins" type="number" size="small" value={packForm.bonus}
              onChange={(e) => setPackForm(prev => ({ ...prev, bonus: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePack} disabled={!packForm.coins || !packForm.price}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoinManagement;
